/**
 * Utilization Orchestrator - Coordinates analysis and optimization phases
 *
 * This module orchestrates the complete utilization optimization process:
 * 1. Analysis Phase: Determine if and how to optimize
 * 2. Execution Phase: Apply changes using proven methods
 * 3. Iteration: Repeat until no more improvements possible
 */

import { DRF_LIMITS } from '~/config/financial-constants';
import { logger } from '~/services/logging';
import type { Candidate, Configuration, Project } from '~/types';
import { UtilizationAnalyzer } from './utilization-analysis';
import { UtilizationOptimizer } from './utilization-optimizer';

/**
 * Configuration for utilization orchestration
 */
export interface UtilizationOrchestrationConfig {
  enableLogging?: boolean;
  maxIterations?: number;
  drfBufferThreshold?: number;
  minPrincipalIncrement?: number;
}

/**
 * Result of complete utilization optimization
 */
export interface UtilizationOrchestrationResult {
  wasOptimized: boolean;
  finalCandidate: Candidate;
  allIterationCandidates: Candidate[];
  iterationsCompleted: number;
  description: string;
  details: Array<{
    iteration: number;
    principalAdded: number;
    paymentDate: string;
    candidateName: string;
  }>;
}

export class UtilizationOrchestrator {
  private analyzer: UtilizationAnalyzer;
  private optimizer: UtilizationOptimizer;
  private config: UtilizationOrchestrationConfig;

  constructor(config: UtilizationOrchestrationConfig = {}) {
    this.config = {
      maxIterations: 5,
      drfBufferThreshold: DRF_LIMITS.DRF_BUFFER_THRESHOLD,
      minPrincipalIncrement: 5000,
      ...config,
    };

    // Initialize analysis and optimization components
    this.analyzer = new UtilizationAnalyzer({
      drfBufferThreshold: this.config.drfBufferThreshold,
      minPrincipalIncrement: this.config.minPrincipalIncrement,
    });

    this.optimizer = new UtilizationOptimizer({
      enableLogging: this.config.enableLogging,
    });
  }

  /**
   * Performs complete utilization optimization with iterative improvements
   */
  optimize(
    candidate: Candidate,
    _project: Project,
    configuration: Configuration
  ): UtilizationOrchestrationResult {
    const allIterationCandidates: Candidate[] = [];
    const details: Array<{
      iteration: number;
      principalAdded: number;
      paymentDate: string;
      candidateName: string;
    }> = [];

    let currentCandidate = candidate;
    let iterationCount = 0;

    if (this.config.enableLogging) {
      logger.L3.debug(`🔧 Starting utilization optimization for ${candidate.name}`);
    }

    // Iterative optimization loop
    for (let iteration = 1; iteration <= this.config.maxIterations!; iteration++) {
      logger.L3.info(
        `🔄 Starting iteration ${iteration} with candidate: "${currentCandidate.name}"`
      );

      // Step 1: Analyze current candidate
      const analysis = this.analyzer.analyzeCandidate(currentCandidate, configuration);

      if (!analysis.canOptimize) {
        if (this.config.enableLogging) {
          logger.L3.debug(`⏹️ Iteration ${iteration}: ${analysis.reason}`);
        }
        break;
      }

      // Step 2: Execute optimization
      const executionResult = this.optimizer.executeOptimization(
        currentCandidate,
        analysis,
        iteration,
        configuration
      );

      if (!executionResult.success) {
        if (this.config.enableLogging) {
          logger.L3.debug(`❌ Iteration ${iteration}: ${executionResult.description}`);
        }
        break;
      }

      // Step 3: Update tracking
      allIterationCandidates.push(executionResult.optimizedCandidate);
      currentCandidate = executionResult.optimizedCandidate;
      iterationCount = iteration;

      // Track details
      if (executionResult.adjustmentDetails) {
        const paymentDate = new Date(executionResult.adjustmentDetails.paymentDate)
          .toISOString()
          .split('T')[0];

        details.push({
          iteration,
          principalAdded: executionResult.adjustmentDetails.totalPrincipalAdded,
          paymentDate,
          candidateName: executionResult.optimizedCandidate.name,
        });
      }

      if (this.config.enableLogging) {
        logger.L3.debug(`✅ Iteration ${iteration}: ${executionResult.description}`);
      }
    }

    // Generate result
    const wasOptimized = iterationCount > 0;
    const finalCandidate = wasOptimized ? currentCandidate : candidate;

    let description: string;
    if (!wasOptimized) {
      description = 'No optimization needed - candidate already well optimized';
    } else if (iterationCount === 1) {
      description = `Applied 1 utilization optimization`;
    } else {
      description = `Applied ${iterationCount} utilization optimizations`;
    }

    if (this.config.enableLogging) {
      if (wasOptimized) {
        logger.domain.optimization(
          `🏁 Optimization complete: ${iterationCount} iterations, created ${allIterationCandidates.length} candidates`
        );
        logger.L3.debug(`📊 Final candidate: ${finalCandidate.name}`);
      } else {
        logger.L3.info(`ℹ️ No optimization applied - candidate already optimal`);
      }
    }

    return {
      wasOptimized,
      finalCandidate,
      allIterationCandidates,
      iterationsCompleted: iterationCount,
      description,
      details,
    };
  }

  /**
   * Performs a single optimization iteration for testing purposes
   */
  optimizeSingleIteration(
    candidate: Candidate,
    project: Project,
    configuration: Configuration
  ): UtilizationOrchestrationResult {
    // Temporarily set max iterations to 1
    const originalMaxIterations = this.config.maxIterations;
    this.config.maxIterations = 1;

    const result = this.optimize(candidate, project, configuration);

    // Restore original config
    this.config.maxIterations = originalMaxIterations;

    return result;
  }

  /**
   * Analyzes a candidate without executing optimization (for testing)
   */
  analyzeOnly(candidate: Candidate, configuration: Configuration) {
    return this.analyzer.analyzeCandidate(candidate, configuration);
  }
}
